import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { usePutAddressMutation } from "@/services/backend/company/address-service";
import type { Address } from "@/services/backend/company/address";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";

const FormSchema = z.object({
  street: z.string().min(1),
  houseNumber: z.string().min(1),
  postbox: z.string(),
  postalCode: z.string().min(1),
  city: z.string().min(1),
  country: z.string().min(1),
  isPrimaryAddress: z.boolean(),
  isInvoicingAddress: z.boolean(),
});

interface EditAddressDialogProps {
  address: Address;
}

export function EditAddressDialog({ address }: EditAddressDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [putAddress, { isLoading, isSuccess, error }] = usePutAddressMutation();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      street: address.street,
      houseNumber: address.houseNumber,
      postbox: address.postbox,
      postalCode: address.postalCode,
      city: address.city,
      country: address.country,
      isPrimaryAddress: address.isPrimaryAddress,
      isInvoicingAddress: address.isInvoicingAddress,
    },
  });

  useEffect(() => {
    function close(): void {
      if (isSuccess) {
        setIsOpen(false);
      }
    }

    close();
  }, [isSuccess]);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    putAddress({ id: address.id, companyId: address.companyId, ...data });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adresse bearbeiten</DialogTitle>
          <DialogDescription>
            Hier können Sie die Adresse des Unternehmens bearbeiten.
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-4"
          >
            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Straße</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="Straße"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="houseNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hausnummer</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="Hausnummer"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postbox"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postfach</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="Postfach"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postleitzahl</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="Postleitzahl"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stadt</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="Stadt"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Land</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} placeholder="Land" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPrimaryAddress"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      disabled={isLoading}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="leading-none">
                    <FormLabel>Hauptadresse</FormLabel>
                    <FormDescription>
                      Ersetzt die bisherige Hauptadresse des Unternehmens.
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isInvoicingAddress"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      disabled={isLoading}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="leading-none">
                    <FormLabel>Rechnungsadresse</FormLabel>
                    <FormDescription>
                      Ersetzt die bisherige Rechnungsadresse des Unternehmens.
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <Button disabled={isLoading} type="submit">
              Speichern
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
